/**
 * One canonical shape for a phone number.
 *
 * Nothing normalised before this: `+79991234567`, `89991234567` and
 * `8 (999) 123-45-67` were three different rows for one person. `Account.phone`
 * carries a UNIQUE index that cannot collapse them, and a contact looked up by
 * the number a client dictated would miss the row saved from an import.
 *
 * Russian numbers land on `+7XXXXXXXXXX`: the leading 8 is the domestic trunk
 * prefix, not part of the number, and a bare 10-digit input is assumed to be
 * Russian because that is the only country this product sells in today.
 * Everything else keeps its own country code.
 */
const RU_NATIONAL_DIGITS = 10;
const RU_FULL_DIGITS = 11;
export function normalizePhone(value) {
    const trimmed = value.trim();
    if (!trimmed)
        return trimmed;
    const hadPlus = trimmed.startsWith("+");
    const digits = trimmed.replace(/\D/g, "");
    if (!digits)
        return trimmed;
    if (hadPlus) {
        // An explicit country code is taken at the caller's word. No 8→7 rewrite
        // here: `+8` + 11 digits is byte-identical to a Japanese +81… number, so
        // "fixing" it would corrupt real foreign numbers.
        return `+${digits}`;
    }
    // 8 999 … is the same number as +7 999 …; storing both splits one person in two.
    if (digits.length === RU_FULL_DIGITS && /^[78]/.test(digits)) {
        return `+7${digits.slice(1)}`;
    }
    if (digits.length === RU_NATIONAL_DIGITS) {
        return `+7${digits}`;
    }
    // Anything else without a plus is not a Russian number and not a declared
    // foreign one — fabricating `+` here would turn visible junk into a
    // canonical-looking wrong number. isValidPhone rejects these shapes, so the
    // gateway never stores them and the cleanup script reports them instead.
    return trimmed;
}
/**
 * Digits a phone must carry to be accepted.
 *
 * An explicit `+` means the caller gave a country code, so short foreign
 * numbers are taken at their word — the previous flat floor of 10 rejected
 * `+1 555 0100`, a valid US number, and there was no way to enter one at all.
 * Without a `+` the value is read as Russian, where anything under ten digits
 * is a typo rather than a short number.
 */
export const PHONE_MIN_DIGITS_INTERNATIONAL = 8;
export const PHONE_MIN_DIGITS_NATIONAL = 10;
export const PHONE_MAX_DIGITS = 15;
/**
 * Ceiling on the raw string, separators included. Counting digits alone leaves
 * a string of punctuation around ten of them passing at any size.
 */
export const PHONE_MAX_LENGTH = 32;
/**
 * Spellings a phone search term should match against stored values.
 *
 * Rows are stored canonically (+7…), but a search box receives whatever a
 * phone book exports — `8 (999) 123-45-67` must still find `+79991234567`.
 * Returns the raw term plus the canonical spelling when the term is a valid
 * phone; anything else passes through untouched, so name searches keep their
 * exact-substring behaviour.
 */
export function phoneSearchTerms(search) {
    if (!isValidPhone(search))
        return [search];
    const canonical = normalizePhone(search);
    return canonical === search ? [search] : [search, canonical];
}
export function isValidPhone(value) {
    if (typeof value !== "string")
        return false;
    if (value.length > PHONE_MAX_LENGTH)
        return false;
    if (!/^\+?[\d\s()-]+$/.test(value))
        return false;
    const digits = value.replace(/\D/g, "");
    if (value.trim().startsWith("+")) {
        return (digits.length >= PHONE_MIN_DIGITS_INTERNATIONAL &&
            digits.length <= PHONE_MAX_DIGITS);
    }
    // Without a plus the value is Russian, and a Russian number is exactly ten
    // national digits, optionally behind the 7/8 trunk prefix. A floor alone
    // accepted 11–15 stray digits that normalizePhone could only mangle.
    return (digits.length === RU_NATIONAL_DIGITS ||
        (digits.length === RU_FULL_DIGITS && /^[78]/.test(digits)));
}
